/**
 * How this firm's telephone system is reached, and what it does with calls.
 *
 * Two doors, shown apart, because they fail apart. ARI carries calls: without it there is
 * no screen-pop and no click-to-call. The extension database carries softphones: without
 * it no dispatcher can be given a telephone, and the ones already registered drop as their
 * registrations expire, because Asterisk consults it on every one. A single "PBX
 * unreachable" light would hide exactly the distinction a support call needs, which is why
 * the test button reports each separately.
 *
 * Neither password can be read back. The form shows whether one is stored and sends a
 * blank unless somebody types a new one — an admin panel that can display a credential is
 * a credential leak with a login form on it. Blank means "leave the stored one alone", so
 * a blank field is not a change and the save bar does not count it as one.
 *
 * One Save saves the lot, so the page carries the `SaveBar` rather than a Save button per
 * card — two requests behind it now, because how the firm's calls ring is its own endpoint,
 * but one bar and one count. The desk phones are the exception: adding one shows its
 * password once, which is an action with a result, not a setting waiting to be saved.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { suggestedTelephoneAddress } from './telephoneAddress';

import {
  outboundLineStrategies,
  settingsApi,
  telephonyLimits,
  type DoorResult,
  type OutboundLineStrategy,
  type TelephonySettings,
  type TelephonyTestResult,
} from '../api';
import { refusal } from '../errors';
import { checkForm, named } from '../check';
import { useListText } from '../listText';
import { CallFlowFields } from './CallFlowFields';
import { DeskPhonesSection } from './DeskPhonesSection';
import { PhoneLinesSection } from './PhoneLinesSection';
import type { PhoneLine } from './lines';
import {
  deskPhonesApi,
  handlingLimits,
  type CallDistribution,
  type CallHandlingSettings,
  type DeskBusyWhen,
} from './deskPhones';
import {
  Banner,
  Button,
  EmptyState,
  FieldErrors,
  Flagged,
  Icon,
  Input,
  LadderRow,
  Measured,
  PageHeader,
  SaveBar,
  Section,
  Select,
  SettingRow,
  Switch,
  type FieldProblem,
} from '../../ui';

export function TelephonyPage() {
  const { t } = useTranslation();
  // What `checkForm` reads before a save: every control on the page, at once (#377).
  const page = useRef<HTMLDivElement>(null);

  /** What the server last told us, which Discard returns to and the change count is read against. */
  const [loaded, setLoaded] = useState<TelephonySettings | null>(null);
  const [draft, setDraft] = useState<TelephonySettings | null>(null);

  /** How the calls ring, and who desk phones belong to: its own endpoint, the same Save. */
  const [handlingLoaded, setHandlingLoaded] = useState<CallHandlingSettings | null>(null);
  const [handling, setHandling] = useState<CallHandlingSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<TelephonyTestResult | null>(null);
  const [error, setError] = useState<FieldProblem | null>(null);
  const [saved, setSaved] = useState(false);

  /** The firm's lines, as the lines section last loaded them: what the order is chosen from. */
  const [lines, setLines] = useState<PhoneLine[] | null>(null);

  const [numbersText, setNumbersText] = useListText(draft?.inboundNumbers ?? [], parseNumbers);

  useEffect(() => {
    void (async () => {
      try {
        const [settings, ringing] = await Promise.all([
          settingsApi.telephony(),
          deskPhonesApi.handling(),
        ]);

        setLoaded(settings);
        setDraft(settings);
        setHandlingLoaded(ringing);
        setHandling(ringing);
      } catch (failure) {
        setError(refusal(failure, t, 'settings.couldNotLoad'));
      }
    })();
  }, [t]);

  // The confirmation is a moment, not a state: it says the save landed and then gets out of
  // the way, rather than sitting there until the next edit and being read as "this is saved"
  // by somebody who has since changed something.
  useEffect(() => {
    if (!saved) return;

    const timer = globalThis.setTimeout(() => setSaved(false), 4000);

    return () => globalThis.clearTimeout(timer);
  }, [saved]);

  const change = useCallback(
    <K extends keyof TelephonySettings>(key: K, value: TelephonySettings[K]) => {
      setSaved(false);
      setDraft((current) => (current === null ? current : { ...current, [key]: value }));
    },
    [],
  );

  const changeHandling = useCallback(
    <K extends keyof CallHandlingSettings>(key: K, value: CallHandlingSettings[K]) => {
      setSaved(false);
      setHandling((current) => (current === null ? current : { ...current, [key]: value }));
    },
    [],
  );

  const handlingChanges = useMemo(
    () =>
      handlingLoaded === null || handling === null
        ? 0
        : (Object.keys(handlingLoaded) as (keyof CallHandlingSettings)[]).filter(
            (key) => handlingLoaded[key] !== handling[key],
          ).length,
    [handlingLoaded, handling],
  );

  /** How many settings differ from what was loaded — the number the save bar counts. */
  const changes = useMemo(
    () => countChanges(loaded, draft) + handlingChanges,
    [loaded, draft, handlingChanges],
  );

  async function save() {
    if (draft === null) return;

    const invalid = checkForm(page.current, t);

    if (invalid !== null) {
      setError(invalid);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Replaced with what the server stored, not with what was typed — which is also what
      // empties the two password boxes again, since a stored credential never comes back.
      const stored = await settingsApi.saveTelephony(draft);

      setLoaded(stored);
      setDraft(stored);

      if (handling !== null && handlingChanges > 0) {
        const ringing = await deskPhonesApi.saveHandling(handling);

        setHandlingLoaded(ringing);
        setHandling(ringing);
      }
      setSaved(true);
      // What was tested a moment ago is no longer what is configured.
      setTest(null);
    } catch (failure) {
      setError(named(refusal(failure, t, 'settings.couldNotSave'), page.current, t));
    } finally {
      setSaving(false);
    }
  }

  async function runTest() {
    setTesting(true);
    setError(null);

    try {
      setTest(await settingsApi.testTelephony());
    } catch (failure) {
      setError(refusal(failure, t, 'telephony.couldNotTest'));
    } finally {
      setTesting(false);
    }
  }

  return (
    <FieldErrors root={page} problem={error} onEdit={() => setError(null)}>
      <PageHeader
        title={t('nav.settings.telephony')}
        description={t('nav.settingsAbout.telephony')}
        actions={
          <Button disabled={testing || draft === null} onClick={() => void runTest()}>
            {testing ? t('telephony.testing') : t('telephony.test')}
          </Button>
        }
      />

      {error !== null && <Banner tone="danger">{error.message}</Banner>}
      {saved && <Banner tone="ok">{t('settings.saved')}</Banner>}

      {test !== null && (
        <>
          <Door label={t('telephony.doorCalls')} result={test.ari} />
          <Door label={t('telephony.doorExtensions')} result={test.extensions} />
        </>
      )}

      {draft === null ? (
        error === null && <EmptyState>{t('app.loading')}</EmptyState>
      ) : (
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Section title={t('telephony.title')}>
            <SettingRow label={t('telephony.enabled')} help={t('telephony.enabledHelp')} last>
              <Switch
                checked={draft.telephonyEnabled}
                label={t('telephony.enabled')}
                onChange={(next) => change('telephonyEnabled', next)}
              />
            </SettingRow>
          </Section>

          <Section title={t('telephony.groupCalls')} description={t('telephony.groupCallsHelp')}>
            <TextSetting
              id="pbxBaseUrl"
              value={draft.pbxBaseUrl}
              placeholder="http://10.0.0.5:8088"
              onChange={(v) => change('pbxBaseUrl', v)}
            />

            <TextSetting
              id="pbxAriUsername"
              value={draft.pbxAriUsername}
              onChange={(v) => change('pbxAriUsername', v)}
            />

            <SecretSetting
              id="pbxAriSecret"
              stored={draft.pbxAriSecretSet}
              value={draft.pbxAriSecret ?? ''}
              onChange={(v) => change('pbxAriSecret', v)}
            />

            <TextSetting
              id="pbxWebSocketUrl"
              value={draft.pbxWebSocketUrl}
              placeholder={suggestedTelephoneAddress(window.location.hostname) ?? 'wss://10.0.0.5:8089/ws'}
              last
              onChange={(v) => change('pbxWebSocketUrl', v)}
            />
          </Section>

          <Section
            title={t('telephony.groupExtensions')}
            description={t('telephony.groupExtensionsHelp')}
          >
            <TextSetting
              id="pbxRealtimeHost"
              value={draft.pbxRealtimeHost}
              placeholder="10.0.0.5"
              onChange={(v) => change('pbxRealtimeHost', v)}
            />

            <SettingRow
              label={t('telephony.pbxRealtimePort')}
              help={t('telephony.pbxRealtimePortHelp')}
            >
              {/* A port has no unit, but it is a number in a column of numbers: `Measured`
                  with nothing beside it keeps the boxes on one edge. */}
              <Measured
                id="pbxRealtimePort"
                type="number"
                inputMode="numeric"
                aria-label={t('telephony.pbxRealtimePort')}
                unit=""
                value={draft.pbxRealtimePort}
                min={telephonyLimits.pbxRealtimePort.min}
                max={telephonyLimits.pbxRealtimePort.max}
                onChange={(e) => change('pbxRealtimePort', e.target.valueAsNumber)}
              />
            </SettingRow>

            <TextSetting
              id="pbxRealtimeDatabase"
              value={draft.pbxRealtimeDatabase}
              placeholder="asterisk"
              onChange={(v) => change('pbxRealtimeDatabase', v)}
            />

            <TextSetting
              id="pbxRealtimeUsername"
              value={draft.pbxRealtimeUsername}
              onChange={(v) => change('pbxRealtimeUsername', v)}
            />

            <SecretSetting
              id="pbxRealtimePassword"
              stored={draft.pbxRealtimePasswordSet}
              value={draft.pbxRealtimePassword ?? ''}
              last
              onChange={(v) => change('pbxRealtimePassword', v)}
            />
          </Section>

          <Section
            title={t('telephony.groupNumbers')}
            description={t('telephony.groupNumbersHelp')}
          >
            <SettingRow
              label={t('telephony.inboundNumbers')}
              help={t('telephony.inboundNumbersHelp')}
            >
              <Input
                id="inboundNumbers"
                type="text"
                aria-label={t('telephony.inboundNumbers')}
                value={numbersText}
                placeholder="+35921234567, +359888123456"
                onChange={(e) => {
                  setNumbersText(e.target.value);
                  change('inboundNumbers', parseNumbers(e.target.value));
                }}
              />
            </SettingRow>

            <SettingRow
              label={t('telephony.outboundLineStrategy')}
              help={t('telephony.outboundLineStrategyHelp')}
              last={draft.outboundLineStrategy === 'ReturnPath'}
            >
              <Select
                id="outboundLineStrategy"
                aria-label={t('telephony.outboundLineStrategy')}
                value={draft.outboundLineStrategy}
                onChange={(e) =>
                  change('outboundLineStrategy', e.target.value as OutboundLineStrategy)
                }
              >
                {outboundLineStrategies.map((strategy) => (
                  <option key={strategy} value={strategy}>
                    {t(`telephony.lineStrategy.${strategy}`)}
                  </option>
                ))}
              </Select>
            </SettingRow>

            {draft.outboundLineStrategy === 'FirmOrder' && (
              <OutboundOrder
                lines={lines}
                order={draft.outboundLines}
                onChange={(next) => change('outboundLines', next)}
              />
            )}
          </Section>

          <PhoneLinesSection onLines={setLines} />

          {handling !== null && (
            <Section
              title={t('telephony.groupRinging')}
              description={t('telephony.groupRingingHelp')}
            >
              <SettingRow label={t('telephony.ringSeconds')} help={t('telephony.ringSecondsHelp')}>
                <Measured
                  id="ringSeconds"
                  type="number"
                  inputMode="numeric"
                  aria-label={t('telephony.ringSeconds')}
                  unit={t('settings.unitSeconds')}
                  value={handling.ringSeconds}
                  min={handlingLimits.ringSeconds.min}
                  max={handlingLimits.ringSeconds.max}
                  onChange={(e) => changeHandling('ringSeconds', e.target.valueAsNumber)}
                />
              </SettingRow>

              <SettingRow
                label={t('telephony.holdRecallSeconds')}
                help={t('telephony.holdRecallSecondsHelp')}
              >
                <Measured
                  id="holdRecallSeconds"
                  type="number"
                  inputMode="numeric"
                  aria-label={t('telephony.holdRecallSeconds')}
                  unit={t('settings.unitSeconds')}
                  value={handling.holdRecallSeconds}
                  min={handlingLimits.holdRecallSeconds.min}
                  max={handlingLimits.holdRecallSeconds.max}
                  onChange={(e) => changeHandling('holdRecallSeconds', e.target.valueAsNumber)}
                />
              </SettingRow>

              <SettingRow
                label={t('telephony.deskPhonesBelongTo')}
                help={t('telephony.deskPhonesBelongToHelp')}
              >
                <Select
                  id="deskPhonesBelongTo"
                  aria-label={t('telephony.deskPhonesBelongTo')}
                  value={handling.deskPhonesBelongTo}
                  onChange={(e) =>
                    changeHandling(
                      'deskPhonesBelongTo',
                      e.target.value as CallHandlingSettings['deskPhonesBelongTo'],
                    )
                  }
                >
                  <option value="Desk">{t('telephony.belongToDesk')}</option>
                  <option value="Dispatcher">{t('telephony.belongToDispatcher')}</option>
                </Select>
              </SettingRow>

              <SettingRow
                label={t('telephony.callDistribution')}
                help={t('telephony.callDistributionHelp')}
              >
                <Select
                  id="callDistribution"
                  aria-label={t('telephony.callDistribution')}
                  value={handling.callDistribution}
                  onChange={(e) =>
                    changeHandling('callDistribution', e.target.value as CallDistribution)
                  }
                >
                  <option value="AllAtOnce">{t('telephony.distributionAllAtOnce')}</option>
                  <option value="TakeTurns">{t('telephony.distributionTakeTurns')}</option>
                  <option value="InOrder">{t('telephony.distributionInOrder')}</option>
                </Select>
              </SettingRow>

              {/* Only a call shared out to one desk at a time asks when a desk is busy, and
                  what happens when it does not answer. */}
              {handling.callDistribution !== 'AllAtOnce' && (
                <>
                  <SettingRow
                    label={t('telephony.deskBusyWhen')}
                    help={t('telephony.deskBusyWhenHelp')}
                  >
                    <Select
                      id="deskBusyWhen"
                      aria-label={t('telephony.deskBusyWhen')}
                      value={handling.deskBusyWhen}
                      onChange={(e) =>
                        changeHandling('deskBusyWhen', e.target.value as DeskBusyWhen)
                      }
                    >
                      <option value="AllPhonesBusy">{t('telephony.busyAllPhones')}</option>
                      <option value="AnyPhoneBusy">{t('telephony.busyAnyPhone')}</option>
                    </Select>
                  </SettingRow>

                  <SettingRow
                    label={t('telephony.nextDesk')}
                    help={t('telephony.nextDeskHelp')}
                  >
                    <Select
                      id="nextDesk"
                      aria-label={t('telephony.nextDesk')}
                      value={handling.nextDeskAfterSeconds === null ? 'alone' : 'next'}
                      onChange={(e) =>
                        changeHandling(
                          'nextDeskAfterSeconds',
                          e.target.value === 'alone' ? null : 10,
                        )
                      }
                    >
                      <option value="alone">{t('telephony.nextDeskNever')}</option>
                      <option value="next">{t('telephony.nextDeskToo')}</option>
                    </Select>
                  </SettingRow>

                  {handling.nextDeskAfterSeconds !== null && (
                    <SettingRow
                      label={t('telephony.nextDeskAfter')}
                      help={t('telephony.nextDeskAfterHelp')}
                    >
                      <Measured
                        id="nextDeskAfterSeconds"
                        type="number"
                        inputMode="numeric"
                        aria-label={t('telephony.nextDeskAfter')}
                        unit={t('settings.unitSeconds')}
                        value={handling.nextDeskAfterSeconds}
                        min={handlingLimits.nextDeskAfterSeconds.min}
                        max={handlingLimits.nextDeskAfterSeconds.max}
                        onChange={(e) =>
                          changeHandling('nextDeskAfterSeconds', e.target.valueAsNumber)
                        }
                      />
                    </SettingRow>
                  )}
                </>
              )}

              <SettingRow
                label={t('telephony.deskPhoneServer')}
                help={t('telephony.deskPhoneServerHelp')}
                last
              >
                <Input
                  id="deskPhoneServer"
                  type="text"
                  aria-label={t('telephony.deskPhoneServer')}
                  value={handling.deskPhoneServer}
                  placeholder="192.168.0.150"
                  onChange={(e) => changeHandling('deskPhoneServer', e.target.value)}
                />
              </SettingRow>
            </Section>
          )}

          {/* Saved as it goes, not through the bar: the mode the list is drawn in is the one
              stored, so a changed mode shows here once it is saved. */}
          {handlingLoaded !== null && (
            <DeskPhonesSection belongTo={handlingLoaded.deskPhonesBelongTo} />
          )}

          <Section
            title={t('telephony.groupCallFlow')}
            description={t('telephony.groupCallFlowHelp')}
          >
            <CallFlowFields
              settings={draft.callFlow}
              onChange={(flow) => change('callFlow', flow)}
            />
          </Section>

          <Section
            title={t('telephony.groupBlocked')}
            description={t('telephony.groupBlockedHelp')}
          >
            <SettingRow
              label={t('telephony.turnBlockedCallersAway')}
              help={t('telephony.turnBlockedCallersAwayHelp')}
              last
            >
              <Switch
                checked={draft.turnBlockedCallersAway}
                label={t('telephony.turnBlockedCallersAway')}
                onChange={(next) => change('turnBlockedCallersAway', next)}
              />
            </SettingRow>
          </Section>

          <Section
            title={t('telephony.groupRecording')}
            description={t('telephony.groupRecordingHelp')}
          >
            <SettingRow
              label={t('telephony.recordCalls')}
              help={t('telephony.recordCallsHelp')}
              last={!draft.recordCalls}
            >
              <Switch
                checked={draft.recordCalls}
                label={t('telephony.recordCalls')}
                onChange={(next) => change('recordCalls', next)}
              />
            </SettingRow>

            {/* Shown either way while recording is on: without the grant it says whom to
                ask, which is the only way an administrator learns it can be had at all. */}
            {draft.recordCalls && (
              <SettingRow
                label={t('telephony.recordWithoutAnnouncement')}
                help={
                  draft.silentRecordingGranted
                    ? t('telephony.recordWithoutAnnouncementHelp')
                    : t('telephony.recordWithoutAnnouncementOnRequest')
                }
              >
                <Switch
                  checked={draft.silentRecordingGranted && draft.recordWithoutAnnouncement}
                  disabled={!draft.silentRecordingGranted}
                  label={t('telephony.recordWithoutAnnouncement')}
                  onChange={(next) => change('recordWithoutAnnouncement', next)}
                />
              </SettingRow>
            )}

            {/* Nothing is kept when nothing is recorded, so the retention is hidden rather
                than shown greyed out. */}
            {draft.recordCalls && (
              <SettingRow
                label={t('telephony.recordingRetentionDays')}
                help={t('telephony.recordingRetentionDaysHelp')}
                last
              >
                <Measured
                  id="recordingRetentionDays"
                  type="number"
                  inputMode="numeric"
                  aria-label={t('telephony.recordingRetentionDays')}
                  unit={t('settings.unitDays')}
                  value={draft.recordingRetentionDays}
                  min={telephonyLimits.recordingRetentionDays.min}
                  max={telephonyLimits.recordingRetentionDays.max}
                  onChange={(e) => change('recordingRetentionDays', e.target.valueAsNumber)}
                />
              </SettingRow>
            )}
          </Section>

          <SaveBar
            changes={changes}
            problem={error?.message}
            busy={saving}
            discardLabel={t('settings.discard')}
            saveLabel={saving ? t('settings.saving') : t('settings.save')}
            onDiscard={() => {
              setSaved(false);
              setError(null);
              setDraft(loaded);
              setHandling(handlingLoaded);
            }}
            onSave={() => void save()}
          >
            {t('settings.unsaved', { count: changes })}
          </SaveBar>
        </form>
      )}
    </FieldErrors>
  );
}

/**
 * One door's result.
 *
 * The API returns an outcome and, when it is not a good one, a code. The words are the
 * client's, like everywhere else, and the two doors get a strip each because a support
 * call turns on which of them answered.
 */
function Door({ label, result }: { label: string; result: DoorResult }) {
  const { t } = useTranslation();

  const ok = result.result === 'Reachable';

  return (
    <Banner tone={ok ? 'ok' : 'danger'}>
      <span className="font-medium">{label}</span>
      {': '}
      {/* The API returns a code and never prose. The dots in it are i18next's own key
          separator, so `telephony.pbx_unavailable` lands in the shared errors bundle
          without any parsing here. */}
      {ok
        ? t('telephony.reachable')
        : t(`errors.${result.code ?? 'server.unexpected'}`, {
            defaultValue: t(`telephony.result.${result.result}`),
          })}
    </Banner>
  );
}

/** A plain text setting, with its label and explanation from its own name. */
function TextSetting({
  id,
  value,
  placeholder,
  last,
  onChange,
}: {
  id: string;
  value: string;
  placeholder?: string;
  last?: boolean;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <SettingRow label={t(`telephony.${id}`)} help={t(`telephony.${id}Help`)} last={last}>
      <Input
        id={id}
        type="text"
        aria-label={t(`telephony.${id}`)}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </SettingRow>
  );
}

/**
 * A password the server will never send back.
 *
 * The field is empty every time this screen opens, whether or not one is stored, and the
 * note underneath is the only thing that says which. Typing nothing leaves the stored one
 * alone, which is what lets somebody correct an address without knowing a password they
 * were never given.
 */
function SecretSetting({
  id,
  stored,
  value,
  last,
  onChange,
}: {
  id: string;
  stored: boolean;
  value: string;
  last?: boolean;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <SettingRow
      label={t(`telephony.${id}`)}
      help={stored ? t('telephony.secretStored') : t('telephony.secretNotStored')}
      last={last}
    >
      <Input
        id={id}
        type="password"
        autoComplete="new-password"
        aria-label={t(`telephony.${id}`)}
        value={value}
        placeholder={stored ? '••••••••' : ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </SettingRow>
  );
}

/**
 * How many settings differ from what was loaded.
 *
 * The call flow is counted field by field rather than as one object: fifteen settings
 * changed under one heading is fifteen changes, and a bar that said "1 change not saved"
 * would be understating what Save is about to do.
 */
/**
 * The firm's order for outgoing calls: which lines, first to last (#328).
 *
 * The dial plan tries them in this order and moves on when one is busy, so the first is the
 * number customers usually see. Built like the dispatch ladder — numbered rows that move up
 * and down, and the lines not in the order below them, each offering to join. A line is shown
 * by what the office calls it and the number on it, because nobody recognises gsm-goip-2. A
 * name in the order whose line has since been removed is left out here and dropped on save.
 */
function OutboundOrder({
  lines,
  order,
  onChange,
}: {
  lines: PhoneLine[] | null;
  order: string[];
  onChange: (order: string[]) => void;
}) {
  const { t } = useTranslation();

  if (lines === null) {
    return <EmptyState>{t('app.loading')}</EmptyState>;
  }

  if (lines.length === 0) {
    return (
      <Banner tone="warn" footer>
        {t('telephony.outboundOrderNoLines')}
      </Banner>
    );
  }

  const byName = new Map(lines.map((line) => [line.name, line]));
  const chosen = order.filter((name) => byName.has(name));
  const unused = lines.filter((line) => !chosen.includes(line.name));

  function move(from: number, by: number) {
    const to = from + by;

    if (to < 0 || to >= chosen.length) return;

    const reordered = [...chosen];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];

    onChange(reordered);
  }

  // One id and one label for the whole list, so a refusal about it — an empty order — is
  // named in the banner, scrolled to and drawn on the list like any other field.
  return (
    <Flagged id="outboundLines" label={t('telephony.outboundOrder')} className="pb-1">
      <div className="px-5 pt-3 pb-1 text-secondary text-ink-3">
        {t('telephony.outboundOrderHelp')}
      </div>

      {chosen.length === 0 && (
        <Banner tone="warn" footer>
          {t('telephony.outboundOrderEmpty')}
        </Banner>
      )}

      {chosen.map((name, index) => {
        const line = byName.get(name)!;

        return (
          <LadderRow
            key={name}
            index={index + 1}
            actions={
              <>
                <Button
                  size="sm"
                  icon
                  disabled={index === 0}
                  aria-label={t('settings.moveUp')}
                  onClick={() => move(index, -1)}
                >
                  <Icon name="arrow-up" small />
                </Button>
                <Button
                  size="sm"
                  icon
                  disabled={index === chosen.length - 1}
                  aria-label={t('settings.moveDown')}
                  onClick={() => move(index, 1)}
                >
                  <Icon name="arrow-down" small />
                </Button>
                <Button
                  size="sm"
                  icon
                  aria-label={t('telephony.outboundOrderRemove')}
                  onClick={() => onChange(chosen.filter((existing) => existing !== name))}
                >
                  <Icon name="close" small />
                </Button>
              </>
            }
          >
            <LineName line={line} />
          </LadderRow>
        );
      })}

      {unused.map((line, index) => (
        <div
          key={line.name}
          className={
            'grid grid-cols-[24px_1fr_auto] gap-3 items-center px-5 py-2.5' +
            (index === unused.length - 1 ? '' : ' border-b border-rule')
          }
        >
          <span />
          <div className="min-w-0 text-ink-3">
            <LineName line={line} />
          </div>
          <Button size="sm" onClick={() => onChange([...chosen, line.name])}>
            {t('telephony.outboundOrderAdd')}
          </Button>
        </div>
      ))}
    </Flagged>
  );
}

/** A line as the office knows it: its label, the number on it, and what it is. */
function LineName({ line }: { line: PhoneLine }) {
  const { t } = useTranslation();

  return (
    <>
      <div className="text-body font-medium">
        {line.label}
        {line.number !== null && (
          <span className="font-mono font-normal text-ink-2 ml-2">{line.number}</span>
        )}
      </div>
      <div className="text-secondary text-ink-3 mt-0.5">
        {t(`phoneLines.kinds.${line.kind}`)} · <span className="font-mono">{line.name}</span>
      </div>
    </>
  );
}

function countChanges(
  loaded: TelephonySettings | null,
  draft: TelephonySettings | null,
): number {
  if (loaded === null || draft === null) return 0;

  const top = (Object.keys(loaded) as (keyof TelephonySettings)[]).filter(
    (key) => key !== 'callFlow' && !same(loaded[key], draft[key]),
  ).length;

  const flow = (Object.keys(loaded.callFlow) as (keyof TelephonySettings['callFlow'])[]).filter(
    (key) => loaded.callFlow[key] !== draft.callFlow[key],
  ).length;

  return top + flow;
}

/**
 * Whether one setting is still what was loaded.
 *
 * The numbers list is compared by its contents, because every keystroke in it builds a new
 * array. A password box is blank both before anybody types and after they clear what they
 * typed, and blank means "leave the stored one alone" — so blank and the null the server
 * sent are the same setting, and neither is a change.
 */
function same(before: unknown, after: unknown): boolean {
  if (Array.isArray(before) && Array.isArray(after)) {
    return before.length === after.length && before.every((value, index) => value === after[index]);
  }

  return (before ?? '') === (after ?? '');
}

/**
 * Reads a list of telephone numbers out of what somebody typed.
 *
 * Forgiving on the way in; the server normalises each one to E.164 against the firm's own
 * calling code and drops anything that is not a number, so what comes back is the truth
 * rather than what was typed.
 */
function parseNumbers(text: string): string[] {
  return text
    .split(/[,;\s]+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}
